import React, { useEffect } from "react";
import { Box, Flex, Radio, RadioGroup, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import PathPickStartGuideExternalLink from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExternalLink";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useExternalLinkGate from "@/features/pathpick/features/start-guide/hooks/useExternalLinkGate";
import {
  SMART_STORE_SELLER_CENTER_URL,
  SMART_STORE_SIGN_UP_URL,
} from "@/features/pathpick/features/start-guide/constants";
import { StartGuideSmartStorePresence } from "@/features/pathpick/features/start-guide/types";

interface Props {
  presence: StartGuideSmartStorePresence | null;
  onChangePresence: (presence: StartGuideSmartStorePresence) => void;
  onConfirm: () => void;
  onReselectProfile: () => void;
}

const HINT_ID = "start-guide-preflight-hint";

// 분기별 문구·링크를 한 곳에 모아 둔다 — 두 분기가 같은 모양이고 값만 다르다.
// 개설 안 한 사용자는 개설 페이지로 보낸다(전달서 §7 이 지정한 URL).
const BRANCHES = {
  has_store: {
    i18nPrefix: "path_pick_guide:preflight.has_store",
    url: SMART_STORE_SELLER_CENTER_URL,
  },
  no_store: {
    i18nPrefix: "path_pick_guide:preflight.no_store",
    url: SMART_STORE_SIGN_UP_URL,
  },
} as const;

/**
 * 스마트스토어 사전 확인 — 판매 방식을 저장할 때마다 설정 목록 앞에서 거치는 관문.
 *
 * 제목과 설명은 페이지 헤더가 들고 있고, 카드에는 선택지만 남는다.
 * 외부 페이지에서 실제로 개설을 마쳤는지는 확인할 수 없으므로, 링크를 한 번 연 뒤에만
 * 완료 표시를 허용한다(전달서 §7). 선택지를 바꾸면 "열었음" 표시도 함께 지운다.
 */
function PathPickStartGuideSmartStorePreflight({
  presence,
  onChangePresence,
  onConfirm,
  onReselectProfile,
}: Props) {
  const { t } = useI18nHelper();
  const { hasOpened, markOpened, reset } = useExternalLinkGate();

  // 개설 여부를 바꾸면 앞 선택지에서 링크를 열었던 사실은 더 이상 의미가 없다.
  useEffect(() => {
    reset();
  }, [presence, reset]);

  const branch = presence ? BRANCHES[presence] : null;

  // 되돌아가기는 버튼이 아니라 링크다 — 호버해도 테두리·배경이 생기지 않고 글자 색만 바뀐다.
  const reselectLink = (
    <Text
      as={"button"}
      type={"button"}
      textStyle={"body-sm-medium"}
      color={"#444440"}
      textDecoration={"underline"}
      textUnderlineOffset={"2px"}
      _hover={{ color: "#1272EF" }}
      onClick={onReselectProfile}
    >
      {t("path_pick_guide:selling_profile.reselect")}
    </Text>
  );

  return (
    <PathPickStartGuideCard>
      <Flex flexDirection={"column"} gap={"16px"}>
        <Box as={"fieldset"}>
          <Text
            as={"legend"}
            textStyle={"body-sm-bold"}
            color={"#22221F"}
            marginBottom={"12px"}
          >
            {t("path_pick_guide:preflight.group_label")}
          </Text>
          <RadioGroup
            value={presence ?? ""}
            onChange={(value) =>
              onChangePresence(value as StartGuideSmartStorePresence)
            }
          >
            <Flex flexDirection={"column"} gap={"12px"}>
              <Radio value={"has_store"}>
                <Text textStyle={"body-sm-medium"} color={"#22221F"}>
                  {t("path_pick_guide:preflight.option_has_store")}
                </Text>
              </Radio>
              <Radio value={"no_store"}>
                <Text textStyle={"body-sm-medium"} color={"#22221F"}>
                  {t("path_pick_guide:preflight.option_no_store")}
                </Text>
              </Radio>
            </Flex>
          </RadioGroup>
        </Box>

        {branch ? (
          <Flex
            role={"group"}
            aria-label={t("path_pick_guide:preflight.group_aria_label")}
            flexDirection={"column"}
            gap={"12px"}
            padding={"16px"}
            borderRadius={"8px"}
            backgroundColor={"#F8F9FB"}
          >
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${branch.i18nPrefix}.title`)}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t(`${branch.i18nPrefix}.description`)}
            </Text>
            <Flex gap={"8px"} flexWrap={"wrap"} alignItems={"center"}>
              <PathPickStartGuideExternalLink
                href={branch.url}
                label={t(`${branch.i18nPrefix}.open_cta`)}
                newWindowLabel={t("path_pick_guide:external_link_suffix")}
                onOpen={markOpened}
                isPrimary={!hasOpened}
              />
              <AppButton
                variant={"primary"}
                isDisabled={!hasOpened}
                onClick={onConfirm}
                chakraProps={
                  hasOpened ? undefined : { "aria-describedby": HINT_ID }
                }
              >
                {t(`${branch.i18nPrefix}.done_cta`)}
              </AppButton>
              {/* 되돌아가기는 주 CTA 오른쪽, 같은 그룹 안에 둔다(데모 구조). */}
              {reselectLink}
            </Flex>
            {!hasOpened && (
              <PathPickStartGuideDisabledHint id={HINT_ID}>
                {t(`${branch.i18nPrefix}.done_disabled_hint`)}
              </PathPickStartGuideDisabledHint>
            )}
          </Flex>
        ) : (
          <Flex justifyContent={"flex-start"}>{reselectLink}</Flex>
        )}
      </Flex>
    </PathPickStartGuideCard>
  );
}

export default PathPickStartGuideSmartStorePreflight;
